import React from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import Checkbox from "@mui/joy/Checkbox";
import Chip from "@mui/joy/Chip";
import CircularProgress from "@mui/joy/CircularProgress";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import Input from "@mui/joy/Input";
import Link from "@mui/joy/Link";
import Option from "@mui/joy/Option";
import Select from "@mui/joy/Select";
import Sheet from "@mui/joy/Sheet";
import Stack from "@mui/joy/Stack";
import Table from "@mui/joy/Table";
import Tooltip from "@mui/joy/Tooltip";
import Typography from "@mui/joy/Typography";
import SearchIcon from "@mui/icons-material/Search";
import { NotificationService } from "../../../common/notification";
import { formatUtcDateTime } from "../../../common/dateTime";
import { Game } from "../../../models/games";
import {
    AdminExternalAccountInfo,
    AdminExternalProviderInfo,
    AdminExternalPurchaseInfo,
    ExternalMarketErrorKindDescription,
    ExternalPurchaseReviewReasonDescription,
    ExternalPurchaseReviewState,
    ExternalPurchaseStatus,
    ExternalPurchaseStatusDescription,
    externalPurchaseStatusColor,
    ExternalTradeStateDescription,
} from "../../../models/externalMarkets";
import { formatMoneyKopecks, fromDatetimeLocalValue, toDatetimeLocalValue, toNum } from "../../bids/analytics/format";
import { extAccountsApi, extPurchasesApi } from "../externalMarketsApi";
import { formatUsd, Pager, resolveGameName } from "../components/shared";

const PageSize = 50;

type Props = {
    provider: AdminExternalProviderInfo;
    games: Game[];
};

type Filters = {
    gameId?: number;
    accountId?: number;
    statuses: ExternalPurchaseStatus[];
    from?: Date;
    to?: Date;
    needsReview: boolean;
};

export function PurchasesTab(props: Props) {
    const { provider, games } = props;
    const navigate = useNavigate();
    const [filters, setFilters] = React.useState<Filters>({ statuses: [], needsReview: false });
    const [applied, setApplied] = React.useState<Filters>({ statuses: [], needsReview: false });
    const [page, setPage] = React.useState(1);
    const [items, setItems] = React.useState<AdminExternalPurchaseInfo[]>([]);
    const [total, setTotal] = React.useState(0);
    const [loading, setLoading] = React.useState(false);
    const [accounts, setAccounts] = React.useState<AdminExternalAccountInfo[]>([]);

    React.useEffect(() => {
        extAccountsApi(provider.key)
            .then((res) => setAccounts(res.result ? res.value ?? [] : []))
            .catch(console.error);
    }, [provider.key]);

    React.useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const res = await extPurchasesApi(provider.key, { ...applied, page, count: PageSize });
                if (cancelled) {
                    return;
                }
                if (res.result && res.value) {
                    setItems(res.value.items ?? []);
                    setTotal(toNum(res.value.total));
                } else {
                    NotificationService.alertError(res.error ?? "Не удалось загрузить журнал выкупов");
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [provider.key, applied, page]);

    const accountLabel = (id?: number) => (id ? accounts.find((a) => a.id === id)?.label ?? `#${id}` : "—");

    return (
        <Stack spacing={1.5}>
            <Box
                sx={{
                    display: "flex",
                    gap: 1.5,
                    flexWrap: "wrap",
                    alignItems: "flex-end",
                    p: 2,
                    bgcolor: "background.level1",
                    borderRadius: "md",
                }}
            >
                <FormControl size="sm" sx={{ minWidth: 150 }}>
                    <FormLabel>Игра</FormLabel>
                    <Select
                        size="sm"
                        value={filters.gameId ?? null}
                        onChange={(_, v) => setFilters({ ...filters, gameId: typeof v === "number" ? v : undefined })}
                    >
                        <Option value={null}>Все</Option>
                        {provider.markets.map((m) => (
                            <Option key={m.gameId} value={m.gameId}>
                                {resolveGameName(games, m.gameId)}
                            </Option>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="sm" sx={{ minWidth: 150 }}>
                    <FormLabel>Аккаунт</FormLabel>
                    <Select
                        size="sm"
                        value={filters.accountId ?? null}
                        onChange={(_, v) => setFilters({ ...filters, accountId: typeof v === "number" ? v : undefined })}
                    >
                        <Option value={null}>Все</Option>
                        {accounts.map((a) => (
                            <Option key={a.id} value={a.id}>
                                {a.label}
                            </Option>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="sm" sx={{ minWidth: 220 }}>
                    <FormLabel>Статусы</FormLabel>
                    <Select
                        size="sm"
                        multiple
                        placeholder="Все"
                        value={filters.statuses}
                        onChange={(_, v) => setFilters({ ...filters, statuses: (v as ExternalPurchaseStatus[]) ?? [] })}
                    >
                        {Object.entries(ExternalPurchaseStatusDescription).map(([value, label]) => (
                            <Option key={value} value={Number(value)}>
                                {label}
                            </Option>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="sm">
                    <FormLabel>С</FormLabel>
                    <Input
                        type="datetime-local"
                        value={filters.from ? toDatetimeLocalValue(filters.from) : ""}
                        onChange={(e) =>
                            setFilters({ ...filters, from: e.target.value ? fromDatetimeLocalValue(e.target.value) : undefined })
                        }
                    />
                </FormControl>
                <FormControl size="sm">
                    <FormLabel>По</FormLabel>
                    <Input
                        type="datetime-local"
                        value={filters.to ? toDatetimeLocalValue(filters.to) : ""}
                        onChange={(e) =>
                            setFilters({ ...filters, to: e.target.value ? fromDatetimeLocalValue(e.target.value) : undefined })
                        }
                    />
                </FormControl>
                <Checkbox
                    size="sm"
                    label="Требует разбора"
                    checked={filters.needsReview}
                    onChange={(e) => setFilters({ ...filters, needsReview: e.target.checked })}
                    sx={{ mb: 0.75 }}
                />
                <Button
                    size="sm"
                    startDecorator={<SearchIcon />}
                    onClick={() => {
                        setPage(1);
                        setApplied({ ...filters });
                    }}
                >
                    Найти
                </Button>
                <Box sx={{ flex: 1 }} />
                <Typography level="body-sm">Найдено: {total}</Typography>
            </Box>

            {loading && items.length === 0 ? (
                <CircularProgress size="sm" />
            ) : items.length === 0 ? (
                <Typography level="body-sm">Выкупов нет.</Typography>
            ) : (
                <Sheet variant="outlined" sx={{ borderRadius: "sm", overflow: "auto" }}>
                    <Table size="sm" hoverRow sx={{ minWidth: 1300 }}>
                        <thead>
                            <tr>
                                <th style={{ width: 60 }}>#</th>
                                <th style={{ width: 80 }}>Заказ</th>
                                <th>Игра</th>
                                <th style={{ width: 170 }}>Статус</th>
                                <th>Аккаунт / бот</th>
                                <th style={{ textAlign: "right" }}>Оплачено</th>
                                <th style={{ textAlign: "right" }}>Себестоимость</th>
                                <th style={{ textAlign: "right" }}>Прибыль план / факт</th>
                                <th>Трейды</th>
                                <th>Создан / обновлён</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((p) => (
                                <tr key={p.id}>
                                    <td>{p.id}</td>
                                    <td>
                                        <Link level="body-sm" onClick={() => navigate(`/app/orders/${p.orderId}`)}>
                                            {p.orderId}
                                        </Link>
                                    </td>
                                    <td>{resolveGameName(games, p.gameId)}</td>
                                    <td>
                                        <Chip size="sm" variant="soft" color={externalPurchaseStatusColor(p.status)}>
                                            {ExternalPurchaseStatusDescription[p.status] ?? p.status}
                                        </Chip>
                                        {p.errorKind !== undefined && p.errorKind !== null && p.status === ExternalPurchaseStatus.Failed && (
                                            <Typography level="body-xs" color="danger">
                                                {ExternalMarketErrorKindDescription[p.errorKind] ?? p.errorKind}
                                            </Typography>
                                        )}
                                        {p.error && (
                                            <Tooltip title={p.error} variant="soft" sx={{ maxWidth: 520 }}>
                                                <Typography
                                                    level="body-xs"
                                                    sx={{ maxWidth: 170, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                                >
                                                    {p.error}
                                                </Typography>
                                            </Tooltip>
                                        )}
                                        {p.attempts > 0 && <Typography level="body-xs">попыток: {p.attempts}</Typography>}
                                        {p.reviewState === ExternalPurchaseReviewState.Open && (
                                            <Tooltip
                                                variant="soft"
                                                sx={{ maxWidth: 420 }}
                                                title={p.reviewReason ? ExternalPurchaseReviewReasonDescription[p.reviewReason]?.hint : undefined}
                                            >
                                                <Chip size="sm" color="warning" variant="solid" sx={{ mt: 0.5 }}
                                                      onClick={() => navigate(`/app/orders/${p.orderId}`)}>
                                                    Разбор: {p.reviewReason
                                                        ? ExternalPurchaseReviewReasonDescription[p.reviewReason]?.label
                                                        : "требуется"}
                                                </Chip>
                                            </Tooltip>
                                        )}
                                        {p.reviewState === ExternalPurchaseReviewState.Resolved && (
                                            <Typography level="body-xs" color="success">разбор закрыт</Typography>
                                        )}
                                    </td>
                                    <td>
                                        <Typography level="body-xs">{accountLabel(p.accountId)}</Typography>
                                        {p.botId ? (
                                            <Link level="body-xs" onClick={() => navigate(`/app/bots/${p.botId}`)}>
                                                бот #{p.botId}
                                            </Link>
                                        ) : (
                                            <Typography level="body-xs">бот —</Typography>
                                        )}
                                    </td>
                                    <td style={{ textAlign: "right" }}>
                                        <Typography level="body-sm">{toNum(p.amountUsd) > 0 ? formatUsd(p.amountUsd) : "—"}</Typography>
                                        {toNum(p.rateUsdRub) > 0 && (
                                            <Typography level="body-xs">курс {toNum(p.rateUsdRub).toLocaleString("ru-RU")}</Typography>
                                        )}
                                    </td>
                                    <td style={{ textAlign: "right" }}>{toNum(p.costRub) > 0 ? formatMoneyKopecks(p.costRub) : "—"}</td>
                                    <td style={{ textAlign: "right" }}>
                                        <Typography level="body-sm">{formatMoneyKopecks(p.plannedProfitRub)}</Typography>
                                        <Typography
                                            level="body-xs"
                                            color={
                                                p.actualProfitRub === undefined || p.actualProfitRub === null
                                                    ? "neutral"
                                                    : toNum(p.actualProfitRub) < toNum(p.plannedProfitRub)
                                                      ? "danger"
                                                      : "success"
                                            }
                                        >
                                            {p.actualProfitRub === undefined || p.actualProfitRub === null
                                                ? "факт —"
                                                : `факт ${formatMoneyKopecks(p.actualProfitRub)}`}
                                        </Typography>
                                    </td>
                                    <td>
                                        {p.providerPurchaseIds.length > 0 && (
                                            <Typography level="body-xs">
                                                Покупка: {p.providerPurchaseIds.join(", ")}
                                            </Typography>
                                        )}
                                        {p.trades.length === 0
                                            ? "—"
                                            : p.trades.map((t) => (
                                                  <Typography key={t.externalTradeId} level="body-xs">
                                                      {t.externalTradeId}
                                                      {t.steamTradeId ? ` / steam ${t.steamTradeId}` : ""} —{" "}
                                                      {ExternalTradeStateDescription[t.state] ?? t.state}
                                                  </Typography>
                                              ))}
                                    </td>
                                    <td>
                                        <Typography level="body-xs">{formatUtcDateTime(p.created)}</Typography>
                                        <Typography level="body-xs">{formatUtcDateTime(p.updated)}</Typography>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                    <Pager page={page} pageCount={Math.max(1, Math.ceil(total / PageSize))} loading={loading} onChange={setPage} />
                </Sheet>
            )}
        </Stack>
    );
}
